import { Link, Text } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { NavLink as RouterLink } from 'react-router-dom';

import { Badge, BadgeStatus } from '@aslanpay/portal-common-lib';

import { ReconciliationCompanyStatusValues } from 'app/constants/reconciliations';

import { ReconciliationCompany } from 'app/types/reconciliations';

import { getCompanyReconciliationProfilePath } from 'app/utils/routes';

/**
 * @description Columns of the companies a reconciliation run covered. Each company links to its own view of the run.
 */
function getReconciliationCompanyColumns(reconciliationId: string) {
  const columnHelper = createColumnHelper<ReconciliationCompany>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<ReconciliationCompany, any>[] = [
    columnHelper.accessor('companyName', {
      header: 'Company',
      enableSorting: false,
      cell: (info) => (
        <Link
          as={RouterLink}
          to={getCompanyReconciliationProfilePath(info.row.original.companyId, reconciliationId)}
          color="brand.purple"
          data-testid={`reconciliation-company-link-${info.row.original.companyId}`}
        >
          {info.getValue() || info.row.original.companyId}
        </Link>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: (info) => {
        const status = ReconciliationCompanyStatusValues[info.getValue() as 'CHECKED' | 'FAILED'];
        return (
          <Badge
            value={status?.value ?? info.getValue()}
            status={(status?.color ?? 'created') as BadgeStatus}
          />
        );
      },
    }),
    columnHelper.accessor('failureReason', {
      header: 'Why it failed',
      enableSorting: false,
      cell: (info) => (
        <Text maxW="360px" whiteSpace="normal">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('aggregatesChecked', {
      header: 'Aggregates checked',
      enableSorting: false,
    }),
    columnHelper.accessor('aggregatesNotVerifiable', {
      header: 'Not verifiable',
      enableSorting: false,
    }),
    columnHelper.accessor('transactionsChecked', {
      header: 'Transactions checked',
      enableSorting: false,
    }),
    columnHelper.accessor('findingsCount', {
      header: 'Problems',
      enableSorting: false,
    }),
    columnHelper.accessor('newFindingsCount', {
      header: 'New',
      enableSorting: false,
    }),
    columnHelper.accessor('resolvedFindingsCount', {
      header: 'Resolved',
      enableSorting: false,
    }),
    columnHelper.accessor('lapsedFindingsCount', {
      header: 'Moved to past',
      enableSorting: false,
      cell: (info) => info.getValue() ?? 0,
    }),
  ];

  return columns;
}

export default getReconciliationCompanyColumns;
